<script lang="ts" setup>
import PageLink from '../components/PageLink.vue'
import Filter from './components/Filter.vue'
import List from './components/List.vue'
import Ad from '../components/Ad.vue'
import Newsletter from '../components/Newsletter.vue'
import { useArticleStore } from '@/stores'
import type { ResponseConfig, IArticleRecipeFilter, IArticle, IHomeHenceCard, Meta } from '@/utils/types'
import useSeo from './seo'
import { computed } from 'vue';

const { advert } = useApi()
const route = useRoute()
const filterQuery = computed(() => {
  return {
    brand: route.query.brand as string,
    tool: route.query.tool as string,
    method: route.query.method as string,
    ingredient: route.query.ingredient as string,
    category: route.query.category as string,
  }
})

const articleStore = useArticleStore()
await articleStore.fetchArticleCategories('projectCategories', {
  className: 'articleProject'
})
await articleStore.fetchRecipeItemCategories()
const categories = computed(() => articleStore.recipeCategories)
const res = ref<ResponseConfig>({
  code: 0,
  data: [] as Array<IArticle>,
  meta: null as any,
  message: ''
})
// 各篩選條件皆為恆食堂分類，以 category_ids[] 帶入 api
const toCategoryIds = (query: Record<string, string>) => {
  return Object.values(query).filter(value => !isEmpty(value))
}

// 品牌另外需以 brand_id 帶入 api，未選擇時回傳 undefined 讓參數不帶出
const toBrandId = (query: { brand: string }) => {
  return isEmpty(query.brand) ? undefined : query.brand
}

const filter = ref<IArticleRecipeFilter>({
  page: 1,
  per_page: 12,
  'category_ids[]': toCategoryIds(filterQuery.value),
  brand_id: toBrandId(filterQuery.value)
})

const articles = computed(() => {
  return res.value.data.map((item: IArticle) => {
    return {
      image: item.image,
      category: item.categories.length > 0 ? item.categories[0].subject : '',
      title: item.subject,
      link: `/hence/recipe/article/${item.id}`
    } as IHomeHenceCard
  })
})

const handleChangePage = async (page: number) => {
  // 換頁時需依目前的篩選條件重新組參數，否則會沿用初始值而失去篩選
  const f = {
    ...filter.value,
    page,
    'category_ids[]': toCategoryIds(filterQuery.value),
    brand_id: toBrandId(filterQuery.value),
  }
  filter.value = f
  const toQuery = {
    page,
    ...filterQuery.value,
  }
  res.value = await articleStore.fetchArticleRecipes(f)
  navigateTo({ path: route.path, query: toQuery })
  window.scrollTo(0, 0)
}

const handleChangeFilterPage = async (page: number, filterQuery: {
  brand: string
  tool: string
  method: string
  ingredient: string
  category: string
}) => {
  const f = {
    ...filter.value,
    page,
    'category_ids[]': toCategoryIds(filterQuery),
    brand_id: toBrandId(filterQuery),
  }
  filter.value = f
  const toQuery = {
    page,
    ...filterQuery,
  }
  res.value = await articleStore.fetchArticleRecipes(f)
  navigateTo({ path: route.path, query: toQuery })
  window.scrollTo(0, 0)
}

handleChangePage(parseInt(route.query.page as string) || 1)

const { data: advertData } = await advert.getAdvert('recipe-list')
const advertRes = computed(() => advertData.value?.data)

useSeo()
</script>

<template>
  <section class="hence">
    <div class="hence__top">
      <div class="hence__top__logo">
        <Nimg src="./images/hence_logo.svg" format="" />
      </div>
      <h1 class="hence__top__title">恆食堂</h1>
      <PageLink />
      <Filter :categories="categories" :filter="filterQuery" :handleChangeFilterPage="handleChangeFilterPage" />
    </div>
    <List :items="articles" :handleChangePage="handleChangePage" :meta="res.meta" />
    <Ad :data="advertRes" v-if="advertRes" />
    <Newsletter class="hence__full" />
  </section>
</template>

<style lang="scss" scoped>
.hence {
  @include max-media(767) {
    position: relative;
    z-index: 2;
    transform: translateY(toRem(100));
    margin-top: toRem(-100);
  }

  &__top {
    display: flex;
    flex-direction: column;
    align-items: center;
    border-bottom: 2px solid $color-gray-line;
    padding-bottom: toRem(32);
    margin-bottom: toRem(60);

    @include max-media(480) {
      margin-bottom: toRem(32);
    }

    &__logo {
      width: 100%;
      max-width: toRem(276);

      img {
        display: block;
        width: 100%;
      }
    }

    &__title {
      margin-bottom: toRem(40);
      font-size: toRem(28);
      font-weight: 500;
      line-height: 1.3;
    }
  }

  &__full {
    margin-left: toRem(-24);
    margin-right: toRem(-24);
  }
}
</style>
